/**
 * GeoTIFF Tools
 *
 * AI agent tools for inspecting GeoTIFF/TIFF raster data:
 * get_geotiff_metadata — reads geo-referencing, band statistics, and location.
 *
 * Decodes on-demand from the ResourceManager blob — no data is held in memory
 * on the Image behavior after texture display.
 */

import { editModeOnly } from './authoring-ops';
import type { ToolDefinition } from './types';
import type { ToolContext } from '../agent-helpers';
import { resolveObject } from '../agent-helpers';
import type { SceneObject, TraceScene } from 'trace-viewer/scene';
import { decodeGeoTiff, isDecodedImageExtension } from 'trace-viewer/scene';
import proj4 from 'proj4';
import {
    INFERENCE_API_URL,
    getGeoTiffBlob,
    inferencePost,
} from '../../inference/shared';
import {
    type RouteAnalyzeResponse,
    type RerouteResponse,
    type GeocodeResponse,
    ROUTE_WARMUP,
    addGlbToScene,
    appendRouteParams,
    parseRouteParams,
    materializeRouteResult,
    getSessionId,
    storeSessionId,
} from '../../inference/route';

// ── Reprojection ──────────────────────────────────────────

/** Well-known EPSG proj4 definitions for CRS commonly found in GeoTIFFs. */
const EPSG_PROJ4: Record<number, string> = {
    // US projections
    5070: '+proj=aea +lat_0=23 +lon_0=-96 +lat_1=29.5 +lat_2=45.5 +x_0=0 +y_0=0 +datum=NAD83 +units=m +no_defs',
    // UTM zones NAD83 (10–19)
    26910: '+proj=utm +zone=10 +datum=NAD83 +units=m +no_defs',
    26911: '+proj=utm +zone=11 +datum=NAD83 +units=m +no_defs',
    26912: '+proj=utm +zone=12 +datum=NAD83 +units=m +no_defs',
    26913: '+proj=utm +zone=13 +datum=NAD83 +units=m +no_defs',
    26914: '+proj=utm +zone=14 +datum=NAD83 +units=m +no_defs',
    26915: '+proj=utm +zone=15 +datum=NAD83 +units=m +no_defs',
    26916: '+proj=utm +zone=16 +datum=NAD83 +units=m +no_defs',
    26917: '+proj=utm +zone=17 +datum=NAD83 +units=m +no_defs',
    26918: '+proj=utm +zone=18 +datum=NAD83 +units=m +no_defs',
    26919: '+proj=utm +zone=19 +datum=NAD83 +units=m +no_defs',
    // UTM zones WGS84 (1, 10–19)
    32601: '+proj=utm +zone=1 +datum=WGS84 +units=m +no_defs',
    32610: '+proj=utm +zone=10 +datum=WGS84 +units=m +no_defs',
    32611: '+proj=utm +zone=11 +datum=WGS84 +units=m +no_defs',
    32612: '+proj=utm +zone=12 +datum=WGS84 +units=m +no_defs',
    32613: '+proj=utm +zone=13 +datum=WGS84 +units=m +no_defs',
    32614: '+proj=utm +zone=14 +datum=WGS84 +units=m +no_defs',
    32615: '+proj=utm +zone=15 +datum=WGS84 +units=m +no_defs',
    32616: '+proj=utm +zone=16 +datum=WGS84 +units=m +no_defs',
    32617: '+proj=utm +zone=17 +datum=WGS84 +units=m +no_defs',
    32618: '+proj=utm +zone=18 +datum=WGS84 +units=m +no_defs',
    32619: '+proj=utm +zone=19 +datum=WGS84 +units=m +no_defs',
    // Web Mercator
    3857: '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +no_defs',
    // Geographic (already lat/lon)
    4326: '+proj=longlat +datum=WGS84 +no_defs',
    4269: '+proj=longlat +datum=NAD83 +no_defs',
};

/**
 * Reproject [x, y] from a projected CRS to WGS84 lon/lat.
 * Returns [lon, lat] or null if the EPSG code is not in the built-in table.
 */
function toWgs84(x: number, y: number, epsg: number): [number, number] | null {
    const srcProj = EPSG_PROJ4[epsg];
    if (!srcProj) return null;
    return proj4(srcProj, 'EPSG:4326', [x, y]) as [number, number];
}

// ── get_geotiff_metadata ──────────────────────────────────

async function executeGetGeotiffMetadata(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const objectRef = input.object as string;

    const target = resolveObject(objectRef, ctx);
    if (!target) return `Error: Object "${objectRef}" not found.`;

    const src = target.src;
    if (!src) return `Error: "${target.name}" has no source.`;

    const ext = src.split('.').pop()?.toLowerCase() ?? '';
    if (!isDecodedImageExtension(ext)) {
        return `Error: "${target.name}" is not a GeoTIFF (src: ${src}).`;
    }

    let blob: Blob;
    try {
        blob = await getGeoTiffBlob(target, ctx.traceScene);
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return `Error: ${msg}`;
    }

    let metadata: Awaited<ReturnType<typeof decodeGeoTiff>>['metadata'];
    let bands: Awaited<ReturnType<typeof decodeGeoTiff>>['bands'];
    try {
        ({ metadata, bands } = await decodeGeoTiff(blob));
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return `Error: Failed to decode GeoTIFF: ${msg}`;
    }
    const geoKeys = metadata.geoKeys;

    // Resolve EPSG code from parsed geoKeys
    const epsg = (geoKeys?.['ProjectedCSTypeGeoKey'] as number | undefined)
        ?? (geoKeys?.['GeographicTypeGeoKey'] as number | undefined);

    // Build result with location info FIRST (most important for the AI)
    const result: Record<string, unknown> = {
        name: target.name,
        src,
    };

    // Location — reproject bounding box to WGS84 lat/lon
    const bbox = metadata.bbox;
    if (bbox && bbox.length === 4 && epsg) {
        result.epsg = epsg;
        const sw = toWgs84(bbox[0], bbox[1], epsg);
        const ne = toWgs84(bbox[2], bbox[3], epsg);
        if (sw && ne) {
            result.center = { lat: Number(((sw[1] + ne[1]) / 2).toFixed(6)), lon: Number(((sw[0] + ne[0]) / 2).toFixed(6)) };
            result.bounds = { south: Number(sw[1].toFixed(6)), west: Number(sw[0].toFixed(6)), north: Number(ne[1].toFixed(6)), east: Number(ne[0].toFixed(6)) };
        } else {
            result.locationNote = `EPSG:${epsg} not in built-in projection database`;
            result.projectedBounds = { minX: bbox[0], minY: bbox[1], maxX: bbox[2], maxY: bbox[3] };
        }
    } else if (bbox) {
        result.locationNote = 'Could not determine EPSG code for reprojection';
        result.projectedBounds = { minX: bbox[0], minY: bbox[1], maxX: bbox[2], maxY: bbox[3] };
    }

    if (geoKeys?.['GTCitationGeoKey']) result.crsName = geoKeys['GTCitationGeoKey'];

    // Raster info
    result.width = metadata.width;
    result.height = metadata.height;
    result.samplesPerPixel = metadata.samplesPerPixel;
    result.bitsPerSample = metadata.bitsPerSample;
    if (metadata.noData !== null) result.noData = metadata.noData;

    // Band statistics (compact)
    result.bands = bands.map((b, i) => {
        let min = Infinity, max = -Infinity;
        for (let j = 0; j < b.length; j++) {
            const v = b[j];
            if (metadata.noData !== null && v === metadata.noData) continue;
            if (v < min) min = v;
            if (v > max) max = v;
        }
        return { index: i, type: b.constructor.name, min: isFinite(min) ? min : null, max: isFinite(max) ? max : null };
    });

    return JSON.stringify(result, null, 2);
}

// ── analyze_route_location ────────────────────────────────

interface LocationInput {
    lat?: number;
    lon?: number;
    radius_km?: number;
    west?: number;
    south?: number;
    east?: number;
    north?: number;
}

async function executeAnalyzeRouteLocation(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const { traceScene } = ctx;

    const lat = input.lat as number | undefined;
    const lon = input.lon as number | undefined;
    const radiusKm = input.radius_km as number | undefined;
    const west = input.west as number | undefined;
    const south = input.south as number | undefined;
    const east = input.east as number | undefined;
    const north = input.north as number | undefined;
    const name = input.name as string | undefined;

    const source = input.source as string | undefined;
    const maxPixels = input.max_pixels as number | undefined;

    const hasCenter = lat != null && lon != null;
    const hasBbox = west != null && south != null && east != null && north != null;

    if (!hasCenter && !hasBbox) {
        return 'Error: Provide either lat + lon (+ optional radius_km), or west + south + east + north for an explicit bounding box.';
    }

    const location: LocationInput = {};
    if (hasCenter) {
        location.lat = lat;
        location.lon = lon;
        location.radius_km = radiusKm ?? 1;
    } else {
        location.west = west;
        location.south = south;
        location.east = east;
        location.north = north;
    }

    const routeParams = parseRouteParams(input);

    try {
        const response = await inferencePost('route', () => {
            const formData = new FormData();
            if (location.lat != null) formData.append('lat', String(location.lat));
            if (location.lon != null) formData.append('lon', String(location.lon));
            if (location.radius_km != null) formData.append('radius_km', String(location.radius_km));
            if (location.west != null) formData.append('west', String(location.west));
            if (location.south != null) formData.append('south', String(location.south));
            if (location.east != null) formData.append('east', String(location.east));
            if (location.north != null) formData.append('north', String(location.north));
            if (source) formData.append('source', source);
            if (maxPixels != null) formData.append('max_pixels', String(maxPixels));
            formData.append('include_glb', 'true');
            appendRouteParams(formData, routeParams);
            return [`${INFERENCE_API_URL}/route/analyze-location`, { method: 'POST', body: formData }];
        }, ROUTE_WARMUP);

        if (!response.ok) {
            const errText = await response.text();
            return `Error: Route analysis failed (${response.status}): ${errText}`;
        }

        const result = await response.json() as RouteAnalyzeResponse;
        const baseName = name ?? (location.lat != null && location.lon != null
            ? `route_${location.lat.toFixed(2)}_${location.lon.toFixed(2)}`
            : 'route_bbox');
        const container = await materializeRouteResult(traceScene, result, baseName);

        if (!container) {
            return 'Error: Route analysis succeeded but returned no GLB meshes.';
        }

        if (name) {
            traceScene.renameSceneObject(container, name);
            // Re-store session under the new name
            if (result.session_id) storeSessionId(container.name, result.session_id);
        }
        ctx.onSceneChanged?.();

        const parts = container.getChildren().map(c => c.name);
        const elevInfo = result.dem
            ? ` Elevation: ${result.dem.elevation_min?.toFixed(0)}m – ${result.dem.elevation_max?.toFixed(0)}m.`
            : '';
        const costInfo = result.route?.cost != null
            ? ` Route cost: ${result.route.cost.toFixed(2)}.`
            : '';
        return `Created "${container.name}" with ${parts.join(' and ')}.${elevInfo}${costInfo}`;
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return `Error: ${msg}`;
    }
}

// ── fetch_terrain ────────────────────────────────────────

async function executeFetchTerrain(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const { traceScene } = ctx;

    const lat = input.lat as number | undefined;
    const lon = input.lon as number | undefined;
    const radiusKm = input.radius_km as number | undefined;
    const west = input.west as number | undefined;
    const south = input.south as number | undefined;
    const east = input.east as number | undefined;
    const north = input.north as number | undefined;
    const source = input.source as string | undefined;
    const maxPixels = input.max_pixels as number | undefined;
    const colormap = input.colormap as string | undefined;
    const vertExag = input.vert_exag as number | undefined;
    const tableWidth = input.table_width as number | undefined;
    const name = input.name as string | undefined;

    const hasCenter = lat != null && lon != null;
    const hasBbox = west != null && south != null && east != null && north != null;

    if (!hasCenter && !hasBbox) {
        return 'Error: Provide either lat + lon (+ optional radius_km), or west + south + east + north for an explicit bounding box.';
    }

    try {
        const response = await inferencePost('route', () => {
            const formData = new FormData();
            if (hasCenter) {
                formData.append('lat', String(lat));
                formData.append('lon', String(lon));
                if (radiusKm != null) formData.append('radius_km', String(radiusKm));
            } else {
                formData.append('west', String(west));
                formData.append('south', String(south));
                formData.append('east', String(east));
                formData.append('north', String(north));
            }
            formData.append('format', 'glb');
            if (source) formData.append('source', source);
            if (maxPixels != null) formData.append('max_pixels', String(maxPixels));
            if (colormap) formData.append('colormap', colormap);
            if (vertExag != null) formData.append('vert_exag', String(vertExag));
            if (tableWidth != null) formData.append('table_width', String(tableWidth));
            return [`${INFERENCE_API_URL}/route/dem`, { method: 'POST', body: formData }];
        }, ROUTE_WARMUP);

        if (!response.ok) {
            const errText = await response.text();
            return `Error: Terrain fetch failed (${response.status}): ${errText}`;
        }

        const sessionId = response.headers.get('X-Session-ID');

        const glbBlob = await response.blob();
        const baseName = name ?? (lat != null && lon != null
            ? `terrain_${lat.toFixed(2)}_${lon.toFixed(2)}`
            : 'terrain_bbox');
        const terrainObject = await addGlbToScene(traceScene, glbBlob, baseName, 'dem');
        if (name) traceScene.renameSceneObject(terrainObject, name);
        // Store session after rename so lookup uses the final name
        if (sessionId) storeSessionId(terrainObject.name, sessionId);
        traceScene.select(terrainObject);
        ctx.onSceneChanged?.();

        const sizeMb = (glbBlob.size / (1024 * 1024)).toFixed(1);
        const sessionNote = sessionId ? ` Session "${sessionId}" cached — use reroute to plan routes on this terrain.` : '';
        return `Created terrain "${terrainObject.name}" (${sizeMb} MB). Elevation-colored mesh from ${source ?? 'usgs3dep'} DEM.${sessionNote}`;
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return `Error: ${msg}`;
    }
}

// ── show_visibility / terrain mode swap ──────────────────

/** Tracks original elevation src so we can swap back without re-fetching. */
const elevationSrcMap = new Map<string, string>();

function findTerrainAndSession(
    input: Record<string, unknown>,
    ctx: ToolContext,
): { terrainObject: SceneObject | null; sessionId: string | null } {
    const terrainRef = input.terrain as string | undefined;
    let sessionId: string | null = null;
    let terrainObject: SceneObject | null = null;

    if (terrainRef) {
        terrainObject = resolveObject(terrainRef, ctx);
        if (terrainObject) {
            sessionId = getSessionId(terrainObject.name) ?? null;
            if (!sessionId) {
                const parent = terrainObject.getParent();
                if (parent) sessionId = getSessionId(parent.name) ?? null;
            }
        }
    }
    if (input.session_id) sessionId = input.session_id as string;
    return { terrainObject, sessionId };
}

async function swapTerrainSrc(
    terrainObject: SceneObject,
    newSrc: string,
    traceScene: TraceScene,
    ctx: ToolContext,
): Promise<void> {
    const data = terrainObject.getSceneObjectData();
    data.src = newSrc;
    await terrainObject.setData(data);
    traceScene.select(terrainObject);
    ctx.onSceneChanged?.();
}

async function executeShowVisibility(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const { traceScene } = ctx;
    const mode = (input.mode as string | undefined) ?? 'visibility';
    const { terrainObject, sessionId } = findTerrainAndSession(input, ctx);

    // Mode: elevation — swap back to original src
    if (mode === 'elevation') {
        if (!terrainObject) return 'Error: Terrain object not found.';
        const originalSrc = elevationSrcMap.get(terrainObject.name);
        if (!originalSrc) return `Error: No original elevation source stored for "${terrainObject.name}". It may already be showing elevation.`;
        await swapTerrainSrc(terrainObject, originalSrc, traceScene, ctx);
        return `Reverted "${terrainObject.name}" to elevation-coded terrain.`;
    }

    // Mode: visibility — fetch visibility GLB and swap
    if (!sessionId) {
        return 'Error: No session found. First use fetch_terrain or analyze_route_location, then reference the terrain by name.';
    }

    try {
        const response = await fetch(`${INFERENCE_API_URL}/route/session/${sessionId}/terrain.glb`);
        if (!response.ok) {
            const errText = await response.text();
            return `Error: Visibility terrain fetch failed (${response.status}): ${errText}`;
        }

        const glbBlob = await response.blob();
        const sizeMb = (glbBlob.size / (1024 * 1024)).toFixed(1);

        if (terrainObject) {
            // Store original src before first swap
            if (!elevationSrcMap.has(terrainObject.name)) {
                elevationSrcMap.set(terrainObject.name, terrainObject.src);
            }
            const assetPath = await traceScene.resources.addAssetWithDedup(
                `assets/${terrainObject.name}_visibility.glb`,
                glbBlob,
            );
            await swapTerrainSrc(terrainObject, `./${assetPath}`, traceScene, ctx);
            return `Swapped "${terrainObject.name}" to visibility heatmap (${sizeMb} MB). Higher intensity = more exposed. Use mode="elevation" to revert.`;
        }

        // Fallback: no terrain ref — create as new object
        const visObject = await addGlbToScene(traceScene, glbBlob, 'visibility', 'terrain');
        traceScene.select(visObject);
        ctx.onSceneChanged?.();
        return `Created visibility terrain "${visObject.name}" (${sizeMb} MB). Higher intensity = more exposed to observers.`;
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return `Error: ${msg}`;
    }
}

// ── geocode ──────────────────────────────────────────────

async function executeGeocode(
    input: Record<string, unknown>,
    _ctx: ToolContext,
): Promise<string> {
    const q = input.q as string;
    const limit = input.limit as number | undefined;

    const response = await inferencePost('route', () => {
        const formData = new FormData();
        formData.append('q', q);
        if (limit != null) formData.append('limit', String(limit));
        return [`${INFERENCE_API_URL}/route/geocode`, { method: 'POST', body: formData }];
    }, ROUTE_WARMUP);

    if (!response.ok) {
        const errText = await response.text();
        return `Error: Geocode failed (${response.status}): ${errText}`;
    }

    const result = await response.json() as GeocodeResponse;
    if (result.candidates.length === 0) {
        return `No results found for "${q}".`;
    }

    return JSON.stringify(result.candidates.map(c => ({
        place: c.place,
        lat: c.lat,
        lon: c.lon,
        type: `${c.osm_class}/${c.osm_type}`,
        bbox: c.bbox,
        radius_km: c.radius_km,
    })), null, 2);
}

// ── reroute ──────────────────────────────────────────────

function findSessionId(input: Record<string, unknown>, ctx: ToolContext): string | null {
    // Explicit session_id
    if (input.session_id) return input.session_id as string;

    // Look up by terrain object name
    const terrainRef = input.terrain as string | undefined;
    if (terrainRef) {
        const target = resolveObject(terrainRef, ctx);
        if (target) {
            // Check the object itself and its parent (for route containers)
            const sid = getSessionId(target.name);
            if (sid) return sid;
            const parent = target.getParent();
            if (parent) {
                const parentSid = getSessionId(parent.name);
                if (parentSid) return parentSid;
            }
        }
    }

    // Try selected object
    const selected = ctx.traceScene.getSelectedObject();
    if (selected) {
        const sid = getSessionId(selected.name);
        if (sid) return sid;
        const parent = selected.getParent();
        if (parent) {
            const parentSid = getSessionId(parent.name);
            if (parentSid) return parentSid;
        }
    }

    return null;
}

async function executeReroute(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const { traceScene } = ctx;

    const sessionId = findSessionId(input, ctx);
    if (!sessionId) {
        return 'Error: No session found. First use fetch_terrain or analyze_route_location to create a terrain, then reference it with "terrain".';
    }

    // Resolve start/stop from scene objects if provided
    const startObj = input.start_object as string | undefined;
    const stopObj = input.stop_object as string | undefined;
    let startX = input.start_x as number | undefined;
    let startZ = input.start_z as number | undefined;
    let stopX = input.stop_x as number | undefined;
    let stopZ = input.stop_z as number | undefined;

    if (startObj) {
        const obj = resolveObject(startObj, ctx);
        if (!obj) return `Error: Start object "${startObj}" not found.`;
        const pos = obj.transform.position;
        startX = pos.x;
        startZ = pos.z;
    }
    if (stopObj) {
        const obj = resolveObject(stopObj, ctx);
        if (!obj) return `Error: Stop object "${stopObj}" not found.`;
        const pos = obj.transform.position;
        stopX = pos.x;
        stopZ = pos.z;
    }

    const startLat = input.start_lat as number | undefined;
    const startLon = input.start_lon as number | undefined;
    const stopLat = input.stop_lat as number | undefined;
    const stopLon = input.stop_lon as number | undefined;
    const responseFormat = input.response_format as string | undefined;

    try {
        const response = await inferencePost('route', () => {
            const formData = new FormData();
            formData.append('session_id', sessionId);
            if (startX != null) formData.append('start_x', String(startX));
            if (startZ != null) formData.append('start_z', String(startZ));
            if (startLat != null) formData.append('start_lat', String(startLat));
            if (startLon != null) formData.append('start_lon', String(startLon));
            if (stopX != null) formData.append('stop_x', String(stopX));
            if (stopZ != null) formData.append('stop_z', String(stopZ));
            if (stopLat != null) formData.append('stop_lat', String(stopLat));
            if (stopLon != null) formData.append('stop_lon', String(stopLon));
            formData.append('response_format', responseFormat ?? 'json');
            return [`${INFERENCE_API_URL}/route/reroute`, { method: 'POST', body: formData }];
        }, ROUTE_WARMUP);

        if (!response.ok) {
            const errText = await response.text();
            return `Error: Reroute failed (${response.status}): ${errText}`;
        }

        // GLB-only response — add route mesh to scene
        if (responseFormat === 'glb') {
            const routeBlob = await response.blob();
            // Find the terrain container to parent the route under
            const terrainRef = input.terrain as string | undefined;
            const parent = terrainRef ? resolveObject(terrainRef, ctx) ?? undefined : undefined;
            const routeObject = await addGlbToScene(traceScene, routeBlob, 'reroute', 'path', parent);
            traceScene.select(routeObject);
            ctx.onSceneChanged?.();
            return `Created route "${routeObject.name}".`;
        }

        const result = await response.json() as RerouteResponse;
        const route = result.route;

        // If response includes a GLB, replace the previous route in the scene
        if (result.glb?.route_glb) {
            const routeBlob = base64ToBlob(result.glb.route_glb);
            const terrainRef = input.terrain as string | undefined;
            const parent = terrainRef ? resolveObject(terrainRef, ctx) ?? undefined : undefined;

            // Remove previous reroute paths (check parent's children and scene root)
            const searchIn = parent
                ? [...parent.getChildren(), ...traceScene.getSceneObjects().filter(o => o.getParent() === null)]
                : traceScene.getSceneObjects();
            const oldRoutes = searchIn.filter(c => c.name.startsWith('reroute_'));
            for (const old of oldRoutes) {
                traceScene.deleteSceneObject(old);
            }

            const routeObject = await addGlbToScene(traceScene, routeBlob, 'reroute', 'path', parent);
            traceScene.select(routeObject);
            ctx.onSceneChanged?.();
        }

        const parts: string[] = [];
        if (route.distance_km != null) parts.push(`${route.distance_km.toFixed(2)} km`);
        parts.push(`cost ${route.cost.toFixed(2)}`);
        parts.push(`${route.num_waypoints} waypoints`);
        const startGeo = route.start_geo ? ` from (${route.start_geo.lat.toFixed(4)}, ${route.start_geo.lon.toFixed(4)})` : '';
        const stopGeo = route.stop_geo ? ` to (${route.stop_geo.lat.toFixed(4)}, ${route.stop_geo.lon.toFixed(4)})` : '';
        return `Route computed: ${parts.join(', ')}${startGeo}${stopGeo}.`;
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return `Error: ${msg}`;
    }
}

function base64ToBlob(base64: string): Blob {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type: 'model/gltf-binary' });
}

// ── Shared schema fragments ──────────────────────────────

const LOCATION_PROPERTIES = {
    lat: { type: 'number' },
    lon: { type: 'number' },
    radius_km: { type: 'number' },
    west: { type: 'number' },
    south: { type: 'number' },
    east: { type: 'number' },
    north: { type: 'number' },
    source: { type: 'string' },
    max_pixels: { type: 'integer' },
};

const START_STOP_PROPERTIES = {
    start_lat: { type: 'number' },
    start_lon: { type: 'number' },
    stop_lat: { type: 'number' },
    stop_lon: { type: 'number' },
};

const ROUTE_PARAM_PROPERTIES = {
    grid_size: { type: 'integer' },
    observer_height: { type: 'number' },
    ...START_STOP_PROPERTIES,
    vert_exag: { type: 'number' },
    table_width: { type: 'number' },
};

// ── Exports ───────────────────────────────────────────────

export const geotiffTools: ToolDefinition[] = [
    {
        tool: {
            name: 'get_geotiff_metadata',
            input_schema: {
                type: 'object' as const,
                properties: {
                    object: { type: 'string' },
                },
                required: ['object'],
            },
        },
        execute: executeGetGeotiffMetadata,
    },
    {
        tool: {
            name: 'geocode',
            input_schema: {
                type: 'object' as const,
                properties: {
                    q: { type: 'string' },
                    limit: { type: 'integer' },
                },
                required: ['q'],
            },
        },
        execute: executeGeocode,
    },
    {
        tool: {
            name: 'fetch_terrain',
            input_schema: {
                type: 'object' as const,
                properties: {
                    ...LOCATION_PROPERTIES,
                    colormap: { type: 'string' },
                    vert_exag: { type: 'number' },
                    table_width: { type: 'number' },
                    name: { type: 'string' },
                },
            },
        },
        execute: editModeOnly(executeFetchTerrain),
    },
    {
        tool: {
            name: 'show_visibility',
            input_schema: {
                type: 'object' as const,
                properties: {
                    terrain: { type: 'string' },
                    mode: { type: 'string' },
                    session_id: { type: 'string' },
                },
            },
        },
        execute: executeShowVisibility,
    },
    {
        tool: {
            name: 'analyze_route_location',
            input_schema: {
                type: 'object' as const,
                properties: {
                    ...LOCATION_PROPERTIES,
                    name: { type: 'string' },
                    ...ROUTE_PARAM_PROPERTIES,
                },
            },
        },
        execute: executeAnalyzeRouteLocation,
    },
    {
        tool: {
            name: 'reroute',
            input_schema: {
                type: 'object' as const,
                properties: {
                    terrain: { type: 'string' },
                    session_id: { type: 'string' },
                    start_object: { type: 'string' },
                    stop_object: { type: 'string' },
                    start_x: { type: 'number' },
                    start_z: { type: 'number' },
                    start_lat: { type: 'number' },
                    start_lon: { type: 'number' },
                    stop_x: { type: 'number' },
                    stop_z: { type: 'number' },
                    stop_lat: { type: 'number' },
                    stop_lon: { type: 'number' },
                    response_format: { type: 'string' },
                },
            },
        },
        execute: executeReroute,
    },
];
